import { useEffect, useState } from "react";
import DropDown from "@/components/forms/inputs/dropDown";
import { DropDownCreatable } from "@/components/forms/inputs/dropDownCreateable";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IDropDownIds, IDropDownOption } from "@/types/DropDown";
import { useTranslation } from "react-i18next";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import {
  Breadcrumbs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { api } from "@/helper/api";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import PeopleIcon from "@mui/icons-material/People";
import CircleIcon from "@mui/icons-material/Circle";
import styles from "@/components/appointmentRequest/createNewAppointmentRequest.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { KAER_COMPANY_SUBJECT_ID } from "@/helper/systemIds";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import {
  AppointmentRequestDepartmentGroup,
  getFlatAppointmentRequestDepartmentOptions,
  groupAppointmentRequestDepartments,
} from "@/components/appointmentRequest/appointmentRequestDepartmentOptions";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";

export type AppointmentRequestType =
  | "standard_single"
  | "standard_multiple"
  | "single_company"
  | "multiple_companies";

type SelectOption<T> = {
  value: T;
  label: string;
};

export function CreateNewAppointmentRequest() {
  const [kaerUserOptions, setKaerUserOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<IDropDownOption[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<
    AppointmentRequestDepartmentGroup[] | any[]
  >([]);
  const [examinationOptions, setExaminationOptions] = useState<
    IDropDownOption[]
  >([]);
  const [appointmentRequestTypeOptions, setAppointmentRequestTypeOptions] =
    useState<SelectOption<AppointmentRequestType>[]>([]);

  const [companySubjectSet, setCompanySubjectSet] = useState<any>([]);
  const [personSubjectSet, setPersonSubjectSet] = useState<any[]>([]);
  const [subjectInfoSet, setSubjectInfoSet] = useState<any[]>([]);
  const [customTitel, setCustomTitle] = useState<string>("");
  const [titleError, setTitleError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "title">({
      fieldOrder: [],
    });
  const [executedBy, setExecutedBy] = useState<any>("");
  const [assistedBy, setAssistedBy] = useState<any>("");
  const [selectedParallelSlots, setSelectedParallelSlots] = useState<number>(1);
  const [selectedSlotDuration, setSelectedSlotDuration] = useState<number>();
  const [categories, setCategories] = useState<any>(null);
  const [appointmentRequestType, setAppointmentRequestType] =
    useState<AppointmentRequestType>();
  const [initialRequestType, setInitialRequestType] =
    useState<AppointmentRequestType>();
  const [examinationsForCategory, setExaminationsForCategory] = useState<any>(
    [],
  );
  const [appointmentRequestStatus, setAppointmentRequestStatus] =
    useState<string>("");
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<any[]>([]);
  const [selectedExaminationIds, setSelectedExaminationIds] =
    useState<IDropDownIds[]>();
  const [selectedMainCategoriesId, setSelectedMainCategoriesId] =
    useState<string>("");

  const [initialDepartmentIds, setInitialDepartmentIds] = useState<
    string[] | null
  >(null);
  const [initialCategoryId, setInitialCategoryId] = useState<string | null>(
    null,
  );

  const [showInvitedSubjectsModalOpen, setShowInvitedSubjectsModalOpen] =
    useState<boolean>(false);
  const { handleError, errorModal } = useErrorModal();

  const navigate = useNavigate();
  const { appointmentrequestId } = useParams() as {
    appointmentrequestId: string;
  };

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    allowSubjectSearchForNonAdmin: false,
    queryParameter: "users",

    subjectIdForFiltering:
      appointmentRequestType === "standard_multiple"
        ? companySubjectSet.map((x: any) => x.value)
        : companySubjectSet[0]?.value,
  });

  const { t } = useTranslation();
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const parallelSlotsOptions: IDropDownOption[] = [];
  for (let value = 1; value <= 5; value += 1) {
    parallelSlotsOptions.push({ value, label: String(value) });
  }

  const [slotDurationOptions, setSlotDurationOptions] = useState<
    IDropDownOption[]
  >(() => {
    const initialSlotDurationOptions: IDropDownOption[] = [
      { value: 5, label: "5" },
      { value: 7, label: "7" },
    ];
    for (let value = 10; value <= 60; value += 5) {
      initialSlotDurationOptions.push({ value, label: String(value) });
    }
    const specificDurations = [
      { value: 75, label: "75" },
      { value: 90, label: "90" },
      { value: 105, label: "105" },
      { value: 120, label: "120 (2h)" },
      { value: 150, label: "150" },
      { value: 180, label: "180 (3h)" },
      { value: 240, label: "240 (4h)" },
      { value: 320, label: "320 (5h)" },
      { value: 400, label: "400 (6h)" },
    ];
    return [...initialSlotDurationOptions, ...specificDurations];
  });

  const isMultiType =
    appointmentRequestType === "multiple_companies" ||
    appointmentRequestType === "standard_multiple";

  const shouldRenderDepartments =
    appointmentRequestType === "standard_single" ||
    appointmentRequestType === "standard_multiple";
  const canEditCompanies =
    !appointmentrequestId ||
    appointmentRequestStatus === "draft" ||
    (appointmentRequestStatus === "open" && shouldRenderDepartments);
  const canChangeType =
    !appointmentrequestId ||
    (["draft", "open"].includes(appointmentRequestStatus) &&
      initialRequestType === "standard_single");
  const availableTypeOptions = appointmentrequestId
    ? appointmentRequestTypeOptions.filter(
        (option) =>
          option.value === initialRequestType ||
          (canChangeType && option.value === "standard_multiple"),
      )
    : appointmentRequestTypeOptions;

  useEffect(() => {
    if (
      selectedSlotDuration &&
      !slotDurationOptions.some(
        (option) => option.value === selectedSlotDuration,
      )
    ) {
      const newOption = {
        value: selectedSlotDuration,
        label: String(selectedSlotDuration),
      };
      setSlotDurationOptions((prevOptions) => [...prevOptions, newOption]);
    }
  }, [selectedSlotDuration, slotDurationOptions]);

  useEffect(() => {
    let isMounted = true;

    const loadinitialData = async () => {
      const [users, types] = await Promise.all([
        getUsers(handleError),
        getAvailableAppointmentRequestTypes(handleError),
      ]);

      if (!isMounted) return;

      const userOptions = users
        .map((user: any) => {
          return {
            value: user.subject_id,
            label: user.last_name + ", " + user.first_name,
          };
        })
        .sort((a: any, b: any) => a.label.localeCompare(b.label));

      const typeOptions = types.map((type: AppointmentRequestType) => ({
        value: type,
        label: t(`appointmentrequest_type_${type}`),
      }));

      setAppointmentRequestTypeOptions(typeOptions);
      setKaerUserOptions(userOptions);
    };

    loadinitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  const parseAppointmentRequest = (data: any) => {
    const departmentIds = data.department_set?.map((d: any) => d.id) ?? [];
    const examinationIds = data.examination_set?.map((e: any) => e.id) ?? [];

    const personSubjectSet =
      data.person_subject_set?.map((s: any) => ({
        value: s.id,
        label: s.name,
        company_id: s.company.id,
        company_subject_id: s.company.subject_id,
      })) ?? [];
    const companySubjectSet =
      data.company_subject_set?.map((s: any) => ({
        value: s.id,
        label: s.name,
        company_id: s.company.id,
        company_customer_id: s.company.customer_id,
      })) ?? [];

    const subjectInfoSet =
      data.subject_info_set
        ?.slice()
        .sort((a: any, b: any) => a.name.localeCompare(b.name)) ?? [];

    let type: AppointmentRequestType = "standard_single";

    type = data?.type;

    return {
      type,
      departmentIds,
      examinationIds,
      personSubjectSet,
      companySubjectSet,
      subjectInfoSet,
      category: data?.category?.id,
      onSite: data?.on_site ?? false,
      preExistingCompanyValue: companySubjectSet,
    };
  };

  useEffect(() => {
    if (!appointmentrequestId) {
      setAppointmentRequestType("standard_single");
      return;
    }

    const getAppointmentRequest = async () => {
      try {
        const res: any = await new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            entity: "appointmentRequest",
            method: "get",
            entityId: appointmentrequestId,
            successHandler: (res: any) => {
              resolve(res);
            },
            failHandler: (error: any) => {
              reject(error);
            },
          });
        });
        if (res.status === 200) {
          const appointmentRequest = res.data;

          const parsed = parseAppointmentRequest(appointmentRequest);

          setAppointmentRequestType(parsed.type);
          setInitialRequestType(parsed.type);
          setInitialCategoryId(parsed.category);
          setAppointmentRequestStatus(appointmentRequest?.status);
          setSelectedExaminationIds(parsed.examinationIds);
          setCompanySubjectSet(parsed.companySubjectSet);
          setPersonSubjectSet(parsed.personSubjectSet);
          setSubjectInfoSet(parsed.subjectInfoSet);

          setCustomTitle(appointmentRequest?.title);
          setSelectedSlotDuration(appointmentRequest?.slot_duration);
          setSelectedParallelSlots(appointmentRequest?.parallel_slots);
          setExecutedBy(appointmentRequest?.executed_by?.id);
          setAssistedBy(appointmentRequest?.assisted_by?.id);

          setInitialDepartmentIds(parsed.departmentIds);
        }
      } catch (error) {
        handleError(error);
      }
    };
    getAppointmentRequest();
  }, [appointmentrequestId]);

  useEffect(() => {
    if (!initialCategoryId) return;
    if (!categoryOptions.length) return;

    setSelectedMainCategoriesId(initialCategoryId);
  }, [initialCategoryId, categoryOptions]);

  useEffect(() => {
    if (!initialDepartmentIds) return;
    if (!departmentOptions.length) return;

    setSelectedDepartmentIds(initialDepartmentIds);
    setInitialDepartmentIds(null);
  }, [initialDepartmentIds, departmentOptions]);

  const formatGroupLabel = (group: AppointmentRequestDepartmentGroup) => (
    <div className={styles.groupOptionsWrapper}>
      <span className={styles.groupOptionsLabel}>{group.label}</span>
      <span>{group.options.length}</span>
    </div>
  );

  useEffect(() => {
    if (!companySubjectSet.length || !shouldRenderDepartments) return;

    const ids = companySubjectSet.map((x: any) => x.value);

    const getDepartments = async () => {
      try {
        const additionalUrlParameters: any = {};

        if (appointmentRequestType === "standard_single") {
          additionalUrlParameters["company_subject_id__in"] = ids;
        } else if (appointmentRequestType === "standard_multiple") {
          additionalUrlParameters["company_subject_id__in"] = ids;
        }

        const res: any = await new Promise<void>((resolve, reject) => {
          api.genericApiRequest({
            entity: "department",
            method: "get",

            successHandler: (res: any) => {
              resolve(res);
            },
            failHandler: (error: any) => {
              reject(error);
            },
            parametersToRender: {
              depth: "0",
              additionalUrlParameters: additionalUrlParameters,
            },
          });
        });
        if (appointmentRequestType === "standard_multiple") {
          setDepartmentOptions(
            groupAppointmentRequestDepartments(res.data.results),
          );
        } else {
          setDepartmentOptions(
            getFlatAppointmentRequestDepartmentOptions(res.data.results).map(
              (option) => ({
                ...option,
                companySubjectId: ids[0],
              }),
            ),
          );
        }
      } catch (error) {
        handleError(error);
      }
    };
    getDepartments();
  }, [companySubjectSet, appointmentRequestType]);

  useEffect(() => {
    const additionalUrlParamameters: any = {};
    additionalUrlParamameters["include_relations"] = true;
    api.genericApiRequest({
      entity: "examination",
      method: "get",
      getAllPages: true,
      parametersToRender: additionalUrlParamameters,
      successHandler: (res: any) => {
        const examinationsForCategory: any = {};
        const categories: any = {};

        for (const key in res.data.results) {
          const examination = res.data.results[key];
          const set = examination.category_set;
          //SET GOT CATEGORY - IDS listed in []
          delete examination.category_set;
          for (const categoryKey in set) {
            const category = set[categoryKey];
            categories[category.id] = category;

            if (!Object.hasOwn(examinationsForCategory, category.id))
              examinationsForCategory[category.id] = [];
            examinationsForCategory[category.id].push(examination);
          }
        }

        setCategories(categories);
        setExaminationsForCategory(examinationsForCategory);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
    });
  }, []);
  //deselect departments of deselected Company
  useEffect(() => {
    if (appointmentRequestType !== "standard_multiple") return;

    if (departmentOptions.length > 0) {
      const availableDepartments = departmentOptions.flatMap(
        (group) => group.options ?? [group],
      );
      setSelectedDepartmentIds((prev) => {
        return prev?.filter((depId) => {
          const option = availableDepartments.find(
            (option) => option.value === depId,
          );
          if (!option) return false;

          return companySubjectSet.some(
            (subject: any) => subject.value === option.companySubjectId,
          );
        });
      });
    }

    setPersonSubjectSet((prevPersons) => {
      return prevPersons.filter((person) =>
        companySubjectSet.some(
          (company: any) => company.company_id === person.company_id,
        ),
      );
    });
  }, [companySubjectSet, departmentOptions, appointmentRequestType]);

  useEffect(() => {
    const tempExaminations: any = [];
    const mainCategoryId = selectedMainCategoriesId;
    for (const examinationKey in examinationsForCategory[mainCategoryId]) {
      const examination =
        examinationsForCategory[mainCategoryId][examinationKey];
      tempExaminations.push({
        value: examination.id,
        label: examination.name,
      });
    }
    tempExaminations.sort((a: any, b: any) => a.label.localeCompare(b.label));
    setExaminationOptions(tempExaminations);
  }, [selectedMainCategoriesId, examinationsForCategory]);

  useEffect(() => {
    if (!categories) return;

    const options = Object.values(categories)
      .map((category: any) => ({
        value: category.id,
        label: category.name,
      }))
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    setCategoryOptions(options);
  }, [categories]);

  // --------------------DropDown OnChangeHandler--------------------
  const saveDropDownOnChangeData = (
    saveMethod: any,
    data: any,
    useCompanyId = false,
  ) => {
    if (data) {
      if (typeof data === "object" && data.value) {
        return saveMethod(data.value);
      }

      if (Array.isArray(data) && data.length > 0) {
        let tempList;
        if (useCompanyId) {
          tempList = data.map((item) => item.companyId);
        } else tempList = data.map((item) => item.value);
        return saveMethod(tempList);
      } else saveMethod([]);
    }
  };

  const companySubjectChange = (selectedOption: any) => {
    if (!selectedOption) {
      return;
    }
    if (Array.isArray(selectedOption)) {
      setCompanySubjectSet(selectedOption);
    } else if (typeof selectedOption === "object") {
      setCompanySubjectSet([selectedOption]);
    }
  };

  const personSubjectSetChange = (selectOptions: any) => {
    if (selectOptions.length) {
      setPersonSubjectSet(selectOptions);
      return;
    }
    setPersonSubjectSet([]);
  };

  const handleDepartmentChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedDepartmentIds, selectedOption);
  };

  const handleCategoryChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedMainCategoriesId, selectedOption);
  };
  const handleAppointmentRequestTypeChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setAppointmentRequestType, selectedOption);
  };

  const handleExaminationChange = (selectedOption: any) => {
    if (selectedOption && selectedOption.length > 0) {
      const examinationIds: any = [];
      const title = selectedOption[0].label;
      for (const key in selectedOption) {
        const id = selectedOption[key].value;
        examinationIds.push(id);
      }
      setSelectedExaminationIds(examinationIds);
      if (!appointmentrequestId) {
        setCustomTitle(title);
      }
    }
  };
  const handleParallelSlotsChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedParallelSlots, selectedOption);
  };
  const handleSlotDurationChange = (selectedOption: any) => {
    saveDropDownOnChangeData(setSelectedSlotDuration, selectedOption);
  };

  const handleTitleChange = (event: any) => {
    event.preventDefault();
    setCustomTitle(event.target.value);
    setTitleError(false);
  };

  const handleResponsibleUserChange = (
    selectedOption: any,
    fieldName: "executed_by" | "assisted_by",
  ) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      if (fieldName === "executed_by") {
        setExecutedBy(selectedOption.value);
      } else {
        setAssistedBy(selectedOption.value);
      }
    }
  };

  // --------------------Open AppointmentDateTime--------------------

  const createAppointmentDraft = async () => {
    if (customTitel === "") {
      setTitleError(true);
      focusFirstInvalidField({}, ["title"]);
      return;
    }

    try {
      const baseCreatePayload: Record<string, any> = {
        title: customTitel,
        type: appointmentRequestType,
      };

      const fullPayload: Record<string, any> =
        appointmentrequestId && appointmentRequestStatus !== "draft"
          ? {
              title: customTitel,
              executed_by: executedBy,
              assisted_by: assistedBy,
            }
          : {
              ...baseCreatePayload,
              category: selectedMainCategoriesId,
              examination_set: selectedExaminationIds,
              slot_duration: selectedSlotDuration,
              parallel_slots: selectedParallelSlots,
              executed_by: executedBy,
              assisted_by: assistedBy,
            };

      //EDIT
      if (appointmentrequestId) {
        if (canEditCompanies) {
          Object.assign(fullPayload, {
            type: appointmentRequestType,
            company_subject_set: companySubjectSet.map(
              (company: any) => company.value,
            ),
            department_set: selectedDepartmentIds,
            person_subject_set: personSubjectSet.map(
              (person: any) => person.value,
            ),
          });
        }
        if (
          appointmentRequestStatus === "draft" &&
          appointmentRequestType === "standard_multiple"
        ) {
          Object.assign(fullPayload, {
            skip_company_validation_for_subject_set: true,
          });
        }
      }
      //CREATE
      else {
        fullPayload.on_site = true;
        if (appointmentRequestType === "multiple_companies") {
          Object.assign(fullPayload, {
            company_subject_set: companySubjectSet.map((x: any) => x.value),
          });
        } else if (appointmentRequestType === "single_company") {
          Object.assign(fullPayload, {
            company_subject_set: companySubjectSet.map((x: any) => x.value),
            person_subject_set: personSubjectSet.map((x: any) => x.value),
          });
        } else if (appointmentRequestType === "standard_multiple") {
          Object.assign(fullPayload, {
            department_set: selectedDepartmentIds,
            skip_company_validation_for_subject_set: true,
            company_subject_set: companySubjectSet.map((x: any) => x.value),
            person_subject_set: personSubjectSet.map((x: any) => x.value),
          });
        } else if (appointmentRequestType === "standard_single") {
          const prepareCompanySubjectSet = () => {
            if (isMultiType) {
              return companySubjectSet.map((x: any) => x.value);
            }
            if (!isMultiType) {
              if (Array.isArray(companySubjectSet)) {
                return companySubjectSet.map((x: any) => x.value);
              }
              if (typeof companySubjectSet === "object") {
                return [companySubjectSet.value];
              }
            }
          };
          Object.assign(fullPayload, {
            department_set: selectedDepartmentIds,
            company_subject_set: prepareCompanySubjectSet(),
            person_subject_set: personSubjectSet.map((x: any) => x.value),
          });
        }
      }
      const config = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response: any = await new Promise((resolve, reject) => {
        api.genericApiRequest({
          entity: "appointmentRequest",
          method: appointmentrequestId ? "put" : "post",
          ...(appointmentrequestId && { entityId: appointmentrequestId }),
          submitData: fullPayload,
          config: config,
          successHandler: (res: any) => {
            resolve(res);
          },
          failHandler: (error: any) => {
            reject(error);
          },
        });
      });

      const id = appointmentrequestId
        ? appointmentrequestId
        : response?.data?.id;
      navigate(`/editappointmentrequest/${id}/building`);
    } catch (error) {
      handleError(error);
    }
  };
  const { handleSubmit, isSubmitting } = useFormSubmission({
    onSubmit: createAppointmentDraft,
  });

  // --------------------Open AppointmentDateTime--------------------

  const disableButtonCheck =
    selectedMainCategoriesId === undefined ||
    selectedMainCategoriesId.length === 0 ||
    selectedExaminationIds === undefined
      ? true
      : false;

  const heading = appointmentrequestId
    ? t("appointment_creation") + " - " + t("edit")
    : t("appointment_creation");
  usePageHeading(heading);

  const buttonTitle = (
    <div className={styles.buttonTitle}>{<PeopleIcon />}</div>
  );

  const disableDepartments = Boolean(
    companySubjectSet.length === 0 ||
      (appointmentrequestId && appointmentRequestStatus !== "draft"),
  );

  //handle ExternalValue in type "standard-multi"
  function getMultiSelectedValues(options: any[], selectedIds: string[]) {
    if (!options || options.length === 0) return [];

    if (options[0]?.options) {
      const flattened = options.flatMap((group: any) => group.options);
      const filtered = flattened.filter((opt: any) =>
        selectedIds.includes(opt.value),
      );
      return filtered;
      // return filtered.map((f: any) => f.value);
    }

    const filteredOptions = options.filter((opt: any) =>
      selectedIds.includes(opt.value),
    );
    return filteredOptions;
    // return filteredOptions.map((x: any) => x.value);
  }

  const departmentValue = getMultiSelectedValues(
    departmentOptions,
    selectedDepartmentIds,
  );

  return (
    <>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit}
        className={styles.container}
        spacing={2}
      >
        <Grid item xs={12}>
          {appointmentrequestId && appointmentRequestStatus !== "draft" && (
            <div role="presentation">
              <Breadcrumbs separator="|">
                <Link
                  className={styles.linkUnderlined}
                  to={`/editappointmentrequest/${appointmentrequestId}/settings`}
                >
                  {t("basic_information")}
                </Link>
                <Link
                  className={styles.linkNoUnderline}
                  to={`/editappointmentrequest/${appointmentrequestId}/building`}
                >
                  {t("building_location")}
                </Link>
                <Link
                  className={styles.linkNoUnderline}
                  to={`/editappointmentrequest/${appointmentrequestId}/calendar`}
                >
                  {t("period")}
                </Link>
              </Breadcrumbs>
            </div>
          )}
          {appointmentRequestStatus !== "draft" && (
            <Tooltip
              title={t("show_invited")}
              className={styles.showInvitedButton}
            >
              <GenericButton
                onClick={() => setShowInvitedSubjectsModalOpen(true)}
              >
                {buttonTitle}
              </GenericButton>
            </Tooltip>
          )}
        </Grid>
        <>
          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-type-field"
              defaultValue={appointmentRequestType}
              label={t("appointmentrequest_type")}
              name={t("appointmentrequest_type")}
              options={availableTypeOptions}
              onChange={handleAppointmentRequestTypeChange}
              disabled={!canChangeType}
            />
          </Grid>
          <Grid item xs={12}>
            <FormFieldLabel
              label={isMultiType ? t("add_single_companies") : t("company")}
              htmlFor="appointment-request-company"
            />
            <DropDownSubjectSearch
              name="appointment-request-company"
              subdomain={subdomain}
              options={[]}
              queryParameter="companies"
              useGlobalState={false}
              isMulti={isMultiType}
              isClearable={true}
              disabled={!canEditCompanies}
              preExistingValue={
                isMultiType ? companySubjectSet : companySubjectSet[0]
              }
              onChange={companySubjectChange}
            />
          </Grid>

          {shouldRenderDepartments && (
            <Grid item xs={12}>
              <DropDown
                dataTestId="appointment-request-department-field"
                label={t("department")}
                name={t("department")}
                defaultValue={departmentValue}
                externalValue={departmentValue}
                options={departmentOptions}
                onChange={handleDepartmentChange}
                disabled={disableDepartments}
                isMulti={true}
                formatGroupLabel={formatGroupLabel}
              />
            </Grid>
          )}
          {(appointmentRequestType === "standard_single" ||
            appointmentRequestType === "standard_multiple") && (
            <Grid item xs={12}>
              <FormFieldLabel
                label={t("add_single_customer")}
                htmlFor="appointment-request-subject"
              />
              <SubjectSearchSelect
                name="appointment-request-subject"
                onChange={personSubjectSetChange}
                isClearable={true}
                isMulti={true}
                loadOptions={(input: any) => fetchSubjects(input)}
                value={personSubjectSet}
                isDisabled={
                  (appointmentRequestType === "standard_multiple" &&
                    companySubjectSet.length === 0) ||
                  (!!appointmentrequestId &&
                    appointmentRequestStatus !== "draft")
                }
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-category-field"
              defaultValue={selectedMainCategoriesId}
              label={t("category")}
              name={t("category")}
              options={categoryOptions}
              onChange={handleCategoryChange}
              disabled={
                appointmentrequestId && appointmentRequestStatus !== "draft"
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-examination-field"
              defaultValue={selectedExaminationIds}
              label={t("examination")}
              name={t("examination")}
              options={examinationOptions}
              onChange={handleExaminationChange}
              isMulti={true}
              disabled={
                !selectedMainCategoriesId ||
                (appointmentrequestId && appointmentRequestStatus !== "draft"
                  ? true
                  : false)
              }
            />
          </Grid>
          <Grid item xs={12} ref={registerValidationTarget("title")}>
            <DefaultInput
              name="name"
              label={t("name")}
              value={customTitel}
              onChange={handleTitleChange}
              mandatory={true}
            />
            {titleError && (
              <Typography color="error" variant="caption" role="alert">
                {t("field_required")}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <DropDownCreatable
              dataTestId="appointment-request-duration-field"
              defaultValue={selectedSlotDuration}
              label={t("duration")}
              name={t("duration")}
              options={slotDurationOptions}
              onChange={handleSlotDurationChange}
              disabled={
                appointmentrequestId && appointmentRequestStatus !== "draft"
                  ? true
                  : false
              }
              isClearable={true}
              updateOptions={(newOption: IDropDownOption) => {
                setSlotDurationOptions((prevOptions) => [
                  ...prevOptions,
                  newOption,
                ]);
                setSelectedSlotDuration(newOption.value as number);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-parallel-slots-field"
              defaultValue={selectedParallelSlots}
              label={t("parallel_slots")}
              name={t("parallel_slots")}
              options={parallelSlotsOptions}
              onChange={handleParallelSlotsChange}
              disabled={
                appointmentrequestId && appointmentRequestStatus !== "draft"
                  ? true
                  : false
              }
            />
          </Grid>
          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-responsible-field"
              defaultValue={executedBy}
              name="executed_by"
              label={t("executed_by")}
              options={kaerUserOptions}
              onChange={(selectedOption) =>
                handleResponsibleUserChange(selectedOption, "executed_by")
              }
              wrapperClassName={styles.dropDown__executedBy}
            />
          </Grid>
          <Grid item xs={12}>
            <DropDown
              dataTestId="appointment-request-assistant-field"
              defaultValue={assistedBy}
              name="assisted_by"
              label={t("assisted_by")}
              options={kaerUserOptions}
              onChange={(selectedOption) =>
                handleResponsibleUserChange(selectedOption, "assisted_by")
              }
              wrapperClassName={styles.dropDown__assistedBy}
            />
          </Grid>

          <Grid item xs={12} className={styles.buttonWrapper}>
            <GenericButton
              variant="outlined"
              onClick={() => navigate(`/appointmentrequest`)}
            >
              {t("back_to", { name: t("appointmentrequest_multi") })}
            </GenericButton>

            {(appointmentRequestStatus !== "open" || canEditCompanies) && (
              <GenericButton
                data-testid="appointment-request-continue-to-building-button"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                waitingForResponse={true}
                showSpinnerOnDisable={customTitel !== "" && !disableButtonCheck}
              >
                {t("continue_to_appointment_building_information")}
              </GenericButton>
            )}
          </Grid>
        </>
      </Grid>
      {errorModal}
      <Dialog
        open={showInvitedSubjectsModalOpen}
        scroll="paper"
        component={"div"}
        onClose={() => setShowInvitedSubjectsModalOpen(false)}
      >
        <DialogTitle textAlign={"center"}>{t("invited_users")}</DialogTitle>
        <DialogContent dividers>
          <DialogContentText
            component={"div"}
            display={"flex"}
            flexDirection={"column"}
          >
            <List>
              {subjectInfoSet
                .filter((subject: any) => subject.invite_sent)
                .map((filteredSubject: any) => (
                  <ListItem key={filteredSubject.id} disablePadding>
                    <ListItemIcon>
                      {filteredSubject.appointment_created ? (
                        <CircleIcon color="success" fontSize="small" />
                      ) : (
                        <CircleIcon color="error" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={filteredSubject.name} />
                  </ListItem>
                ))}
            </List>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <GenericButton
            onClick={() => {
              setShowInvitedSubjectsModalOpen(false);
            }}
          >
            {t("ok_capital")}
          </GenericButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

const getUsers = async (handleError: any) => {
  const additionalUserParameters: any = {};
  additionalUserParameters["impersonate_subject"] = KAER_COMPANY_SUBJECT_ID;
  additionalUserParameters["is_active"] = "true";

  try {
    const res: any = await new Promise<any>((resolve, reject) => {
      api.genericApiRequest({
        entity: "user",
        method: "get",
        parametersToRender: {
          additionalUrlParameters: additionalUserParameters,
          depth: "0",
        },

        successHandler: (res: any) => {
          resolve(res);
        },
        failHandler: (error: any) => {
          reject(error);
        },
      });
    });

    return res.data.results;
  } catch (error) {
    handleError(error);
    return [];
  }
};

const getAvailableAppointmentRequestTypes = async (handleError: any) => {
  try {
    const res: any = await new Promise<any>((resolve, reject) => {
      api.genericApiRequest({
        entity: "appointmentRequest",
        method: "get",
        additionalRouteParts: { available_types: "/" },

        successHandler: (res: any) => {
          resolve(res);
        },
        failHandler: (error: any) => {
          reject(error);
        },
      });
    });

    return res.data.results;
  } catch (error) {
    handleError(error);
    return [];
  }
};
